import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import type { EventOverview } from '@peles/shared';
import { eventTabPath } from './eventTabs';

/** How long after creation a new event offers its first steps. */
export const GETTING_STARTED_DAYS = 30;

export interface GettingStartedStep {
  id: 'client' | 'vendors' | 'timeline';
  label: string;
  hint: string;
  done: boolean;
  /** A tab to go to, or the Clients card on this page. */
  target: 'vendors' | 'timeline' | 'clients';
}

/** The first three things a new event needs, and which are done. */
export function gettingStartedSteps(overview: EventOverview): GettingStartedStep[] {
  return [
    {
      id: 'client',
      label: 'Add the client',
      hint: 'Who the event is for, so they can follow it in their portal.',
      done: overview.clientCount > 0,
      target: 'clients',
    },
    {
      id: 'vendors',
      label: 'Add vendors',
      hint: 'Caterer, photographer, florist — everyone you are booking.',
      done: overview.vendorCount > 0,
      target: 'vendors',
    },
    {
      id: 'timeline',
      label: 'Build the timeline',
      hint: 'The run of the day, from set-up to the last dance.',
      done: overview.timelineCount > 0,
      target: 'timeline',
    },
  ];
}

const storageKey = (eventId: string) => `event:getting-started:dismissed:${eventId}`;

function readDismissed(eventId: string): boolean {
  try {
    return localStorage.getItem(storageKey(eventId)) === '1';
  } catch {
    return false;
  }
}

/** True while an event is new enough for the card to be worth showing. */
export function isNewEvent(createdAt: Date | string | undefined, now = new Date()): boolean {
  if (!createdAt) return false;
  const age = now.getTime() - new Date(createdAt).getTime();
  return age >= 0 && age < GETTING_STARTED_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * The first steps of a newly created event, ticked off as they happen. It
 * disappears once all three are done or when dismissed, and an event older
 * than a month never shows it — an established event without a timeline is a
 * choice, not a forgotten step.
 */
export function GettingStartedCard({
  eventId,
  createdAt,
  overview,
}: {
  eventId: string;
  createdAt?: Date | string;
  overview: EventOverview;
}) {
  const [dismissed, setDismissed] = useState(() => readDismissed(eventId));
  const steps = gettingStartedSteps(overview);
  const doneCount = steps.filter((s) => s.done).length;

  if (dismissed || doneCount === steps.length || !isNewEvent(createdAt)) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(storageKey(eventId), '1');
    } catch {
      // Private mode: it stays hidden for this visit only.
    }
    setDismissed(true);
  };

  return (
    <section
      aria-label="Getting started"
      className="theme-light rounded-2xl bg-gradient-to-br from-primary-600 to-primary-800 text-white p-5 md:p-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold">Get this event going</h2>
          <p className="text-sm text-primary-100 mt-0.5">
            {doneCount} of {steps.length} done
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss getting started"
          className="p-1.5 rounded-lg text-primary-100 hover:bg-white/10 hover:text-white"
        >
          <X size={18} />
        </button>
      </div>
      <ol className="mt-4 grid gap-3 md:grid-cols-3">
        {steps.map((step, i) => {
          const body = (
            <>
              <span
                className={`w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${
                  step.done ? 'bg-white text-primary-700' : 'border border-white/50'
                }`}
              >
                {step.done ? <Check size={14} aria-label="Done" /> : i + 1}
              </span>
              <span className="min-w-0">
                <span
                  className={`block font-semibold ${step.done ? 'line-through opacity-70' : ''}`}
                >
                  {step.label}
                </span>
                <span className="block text-xs text-primary-100 mt-0.5">{step.hint}</span>
              </span>
            </>
          );
          const cls =
            'h-full flex items-start gap-3 rounded-xl bg-white/10 p-3 text-left text-sm hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white';
          return (
            <li key={step.id}>
              {step.target === 'clients' ? (
                <button
                  type="button"
                  className={`w-full ${cls}`}
                  onClick={() =>
                    document
                      .getElementById('event-clients')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                  }
                >
                  {body}
                </button>
              ) : (
                <Link to={eventTabPath(eventId, step.target)} className={cls}>
                  {body}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
